/**
 * Whatnot listing titles are one combined, search-optimised string:
 *
 *   `(VG) Borat (1269) 2022 Toy Tokyo NYCC Sticker | Movies Borat`
 *    ─owner ─name ─box# ─year ──exclusivity── ─features─   ─license series
 *
 * Inventory stores those parts as separate columns, so the two directions need
 * one shared definition: `parseWhatnotTitle` cleans an incoming title into
 * fields, `buildWhatnotTitle` recombines fields into a title for Whatnot.
 * Both live here (not in `@farnsworth/db`) because the API publish path and the
 * CSV export path must agree byte for byte.
 */
import { FUNKO_STICKER_TYPES } from "../constants/index.js";
import { FUNKO_FEATURES, normalizeFeatureName } from "./features.js";

/** Whatnot caps listing titles at 150 characters. */
export const WHATNOT_TITLE_MAX = 150;

/** Fields `buildWhatnotTitle` reads. `Item` from `@farnsworth/db` satisfies it. */
export interface WhatnotTitleSource {
  name: string;
  owner?: string | null;
  boxNo?: string | null;
  license?: string | null;
  series?: string | null;
  exclusiveTo?: string | null;
  /** Release year; falls back to `metadata.year` when omitted. */
  year?: string | number | null;
  /** Normalized feature labels (Glitter, Glow-in-the-Dark, NYCC, …). */
  features?: readonly string[] | null;
  /** Read for the parsed year and any title residue kept on import. */
  metadata?: Record<string, unknown> | null;
}

export interface ParsedWhatnotTitle {
  /** Leading `(VG)` marker. Only an alphabetic prefix counts; `(1269)` is a box number. */
  owner: string | null;
  name: string;
  boxNo: string | null;
  year: string | null;
  /** Feature/sticker labels recognised in the title, normalized to shared vocabulary. */
  features: string[];
  isChase: boolean;
  isVaulted: boolean;
  /** Exclusivity/series words left over once name, box #, year and features are removed. */
  keywords: string;
  /** Everything after the first `|` — normally `license series`. */
  suffix: string;
}

const OWNER_PREFIX = /^\(\s*([A-Za-z][A-Za-z0-9]{0,4})\s*\)\s*/;
const BOX_TOKEN = /\(\s*(\d{1,5})\s*\)/;
const YEAR_TOKEN = /\b((?:19|20)\d{2})\b/;
const CHASE_TOKEN = /\b(chase|vault(?:ed)?)\b/i;
const EDGE_PUNCTUATION = /^[\s,;:|/\\·•\-–—]+|[\s,;:|/\\·•\-–—]+$/g;

function collapse(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/**
 * Canonical label for a title word, or null when it is ordinary prose.
 * `glow` -> Glow-in-the-Dark, `nycc` -> NYCC, `sticker` -> null (stays in keywords).
 */
function canonicalFeatureLabel(token: string): string | null {
  const trimmed = token.trim();
  if (!trimmed) return null;
  const sticker = FUNKO_STICKER_TYPES.find((value) => value.toLowerCase() === trimmed.toLowerCase());
  const normalized = normalizeFeatureName(sticker ?? trimmed);
  if ((FUNKO_FEATURES as readonly string[]).includes(normalized)) return normalized;
  // An alias mapping (glow -> Glow-in-the-Dark, common -> Common) means it is
  // known vocabulary even though it is not a listed feature.
  if (normalized.toLowerCase() !== (sticker ?? trimmed).toLowerCase()) return normalized;
  // SDCC / NYCC / ECCC keep the enum's spelling rather than the title's case.
  return sticker ?? null;
}

interface Phrase {
  pattern: RegExp;
  label: string;
}

/**
 * Vocabulary matched as whole phrases, longest first so `Glow-in-the-Dark` is
 * consumed before `glow` can bite a piece out of it.
 */
const PHRASES: Phrase[] = (() => {
  const seen = new Set<string>();
  const phrases: Phrase[] = [];
  for (const raw of [...FUNKO_FEATURES, ...FUNKO_STICKER_TYPES]) {
    const label = canonicalFeatureLabel(raw);
    if (!label) continue;
    const key = raw.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const body = raw.trim()
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/[\s-]+/g, "[\\s-]+");
    phrases.push({
      pattern: new RegExp(`(?<![\\p{L}\\p{N}])${body}(?![\\p{L}\\p{N}])`, "iu"),
      label,
    });
  }
  return phrases.sort((a, b) => b.label.length - a.label.length || a.label.localeCompare(b.label));
})();

/**
 * Lift recognised feature words out of `text`, returning the cleaned text and
 * the labels in the order they appeared. Overlapping matches keep the longest
 * (`Glow-in-the-Dark` wins over `glow`), and labels are de-duplicated.
 */
function extractFeatures(text: string): { text: string; labels: string[] } {
  const found: Array<{ index: number; length: number; label: string }> = [];
  for (const phrase of PHRASES) {
    const match = phrase.pattern.exec(text);
    if (match) found.push({ index: match.index, length: match[0].length, label: phrase.label });
  }
  if (found.length === 0) return { text, labels: [] };

  found.sort((a, b) => a.index - b.index || b.length - a.length);
  const labels: string[] = [];
  let lastEnd = -1;
  for (const match of found) {
    if (match.index < lastEnd) continue;
    lastEnd = match.index + match.length;
    if (!labels.includes(match.label)) labels.push(match.label);
  }

  let cleaned = text;
  for (const phrase of PHRASES) cleaned = cleaned.split(phrase.pattern).join(" ");
  return { text: cleaned, labels };
}

/** Read the residue an earlier import kept on the item, if any. */
function readImportResidue(metadata: Record<string, unknown> | null | undefined): {
  keywords: string;
  suffix: string;
} {
  const raw = metadata?.whatnotImport;
  if (!raw || typeof raw !== "object") return { keywords: "", suffix: "" };
  const record = raw as Record<string, unknown>;
  const read = (key: string): string =>
    typeof record[key] === "string" ? collapse(String(record[key]).replace(EDGE_PUNCTUATION, "")) : "";
  return { keywords: read("keywords"), suffix: read("suffix") };
}

function readYear(metadata: Record<string, unknown> | null | undefined): string {
  const raw = metadata?.year;
  if (typeof raw === "number" && Number.isFinite(raw)) return String(Math.trunc(raw));
  if (typeof raw === "string") {
    const match = YEAR_TOKEN.exec(raw.trim());
    if (match) return match[1]!;
  }
  return "";
}

/**
 * Split a Whatnot title into inventory fields.
 *
 * Conservative by design: only the owner prefix, a parenthesised box number, a
 * four-digit year and known feature words are treated as structure. Everything
 * else stays in `keywords` (or the name) so no part of the title is destroyed —
 * a GT Poppedia match can then file the residue properly.
 */
export function parseWhatnotTitle(raw: string): ParsedWhatnotTitle {
  const parsed: ParsedWhatnotTitle = {
    owner: null,
    name: "",
    boxNo: null,
    year: null,
    features: [],
    isChase: false,
    isVaulted: false,
    keywords: "",
    suffix: "",
  };

  const title = collapse(String(raw ?? ""));
  if (!title) return parsed;

  let rest = title;
  const ownerMatch = OWNER_PREFIX.exec(rest);
  if (ownerMatch) {
    parsed.owner = ownerMatch[1]!;
    rest = rest.slice(ownerMatch[0].length).trim();
  }

  const pipeIndex = rest.indexOf("|");
  const head = (pipeIndex >= 0 ? rest.slice(0, pipeIndex) : rest).trim();
  parsed.suffix = pipeIndex >= 0 ? collapse(rest.slice(pipeIndex + 1)) : "";

  // The box number is the only reliable boundary between name and everything else.
  let nameRegion = head;
  let tailRegion = "";
  const boxMatch = BOX_TOKEN.exec(head);
  if (boxMatch) {
    parsed.boxNo = boxMatch[1]!;
    const before = head.slice(0, boxMatch.index).trim();
    const after = head.slice(boxMatch.index + boxMatch[0].length).trim();
    if (before) {
      nameRegion = before;
      tailRegion = after;
    } else {
      // `(1269) Borat …` — the name follows the box number.
      nameRegion = after;
    }
  }

  const keywords: string[] = [];

  // With no box number to cut on, a year sitting mid-title is the next best
  // boundary: everything after it is residue, not name.
  if (!tailRegion) {
    const midYear = YEAR_TOKEN.exec(nameRegion);
    if (midYear && midYear.index > 0) {
      parsed.year = midYear[1]!;
      tailRegion = nameRegion.slice(midYear.index + midYear[0].length);
      nameRegion = nameRegion.slice(0, midYear.index);
    }
  }

  // A year is unambiguous wherever it sits, so it is lifted out of whichever
  // region holds it — including the name, when there is no box number to cut on.
  if (!parsed.year) {
    const yearRegion = tailRegion || nameRegion;
    const yearMatch = YEAR_TOKEN.exec(yearRegion);
    if (yearMatch) {
      parsed.year = yearMatch[1]!;
      if (tailRegion) tailRegion = tailRegion.replace(YEAR_TOKEN, " ");
      else nameRegion = nameRegion.replace(YEAR_TOKEN, " ");
    }
  }

  // Chase/vault markers are variant flags, but they still have to survive the
  // round trip, so they are flagged *and* kept as residue rather than dropped.
  const chaseMatch = CHASE_TOKEN.exec(nameRegion);
  if (chaseMatch) {
    if (/^vault/i.test(chaseMatch[1]!)) parsed.isVaulted = true;
    else parsed.isChase = true;
    keywords.push(nameRegion.slice(chaseMatch.index));
    nameRegion = nameRegion.slice(0, chaseMatch.index);
  }
  const tailChase = CHASE_TOKEN.exec(tailRegion);
  if (tailChase) {
    if (/^vault/i.test(tailChase[1]!)) parsed.isVaulted = true;
    else parsed.isChase = true;
  }

  // Feature words are only lifted out of the tail when there is one: the words
  // before a box number belong to the name and must not be re-labelled.
  if (tailRegion) {
    const extracted = extractFeatures(tailRegion);
    tailRegion = extracted.text;
    parsed.features = extracted.labels;
    keywords.push(tailRegion);
  } else {
    const extracted = extractFeatures(nameRegion);
    nameRegion = extracted.text;
    parsed.features = extracted.labels;
  }

  parsed.name = collapse(nameRegion).replace(EDGE_PUNCTUATION, "");
  parsed.keywords = collapse(keywords.join(" ")).replace(EDGE_PUNCTUATION, "");

  if (!parsed.name) {
    // Nothing left that could be a name: fall back rather than drop the row.
    parsed.name = parsed.suffix || collapse(head) || title;
    if (parsed.name === title) parsed.keywords = "";
  }

  return parsed;
}

/**
 * True when `candidate` is the leading, word-boundary portion of `current`,
 * i.e. a GT Poppedia name that explains the start of a longer parsed name.
 */
export function isNamePrefix(candidate: string, current: string): boolean {
  const left = collapse(candidate).toLowerCase();
  const right = collapse(current).toLowerCase();
  if (!left || !right || left.length >= right.length) return left === right;
  if (!right.startsWith(left)) return false;
  const next = right.charAt(left.length);
  return !/[a-z0-9]/.test(next);
}

function truncateAtWord(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  // Only fall back to a word boundary when it keeps most of the budget.
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(EDGE_PUNCTUATION, "").trim();
}

function metadataTokens(...values: Array<string | null | undefined>): Set<string> {
  const tokens = new Set<string>();
  for (const value of values) {
    for (const token of String(value ?? "").toLowerCase().match(/[a-z0-9]+/g) ?? []) {
      tokens.add(token);
    }
  }
  return tokens;
}

/**
 * Recombine inventory fields into one searchable Whatnot title.
 *
 * Order mirrors the titles operators already write by hand: owner, name, box
 * number, year, exclusivity, features, leftover keywords, then `license series`
 * after the pipe. Residue from an import is only emitted when the structured
 * fields do not already cover it, and the title never exceeds Whatnot's limit.
 */
export function buildWhatnotTitle(source: WhatnotTitleSource): string {
  const owner = collapse(String(source.owner ?? "")).replace(/[()]/g, "").trim();
  const name = collapse(String(source.name ?? "")).replace(EDGE_PUNCTUATION, "") || "Untitled";
  const boxNo = collapse(String(source.boxNo ?? "")).replace(/[()#]/g, "");
  const year = collapse(String(source.year ?? "")) || readYear(source.metadata);
  const exclusiveTo = collapse(String(source.exclusiveTo ?? ""));
  const features = (source.features ?? [])
    .map((feature) => collapse(String(feature)).replace(EDGE_PUNCTUATION, ""))
    .filter(Boolean);
  const residue = readImportResidue(source.metadata);
  const license = collapse(String(source.license ?? ""));
  const series = collapse(String(source.series ?? ""));

  const left: string[] = [];
  if (owner) left.push(`(${owner})`);
  left.push(name);
  if (boxNo) left.push(`(${boxNo})`);
  if (year) left.push(year);

  // Everything already on the left is emitted at most once: a GT Poppedia
  // exclusivity that repeats a feature word must not double it in the title.
  const covered = metadataTokens(name, boxNo, year);
  if (exclusiveTo) {
    left.push(exclusiveTo);
    for (const token of metadataTokens(exclusiveTo)) covered.add(token);
  }
  for (const feature of features) {
    const tokens = feature.toLowerCase().match(/[a-z0-9]+/g) ?? [];
    if (tokens.length > 0 && tokens.every((token) => covered.has(token))) continue;
    left.push(feature);
    for (const token of tokens) covered.add(token);
  }

  for (const word of residue.keywords.split(/\s+/).filter(Boolean)) {
    const token = word.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (token.length >= 2 && covered.has(token)) continue;
    left.push(word);
    covered.add(token);
  }

  const structured = [license, series].filter(Boolean).join(" ");
  const right = structured || residue.suffix;

  const join = (leftParts: string[]): string => collapse(leftParts.join(" "));
  let title = right ? `${join(left)} | ${right}` : join(left);

  if (title.length > WHATNOT_TITLE_MAX && right) {
    const budget = WHATNOT_TITLE_MAX - right.length - 3;
    if (budget > 20) title = `${truncateAtWord(join(left), budget)} | ${right}`;
  }
  if (title.length > WHATNOT_TITLE_MAX) title = truncateAtWord(title, WHATNOT_TITLE_MAX);
  return title;
}
